/**
 * iter150AZ.61 — AuthVideo: authenticated `<video>` playback.
 *
 * Companion to ``AuthImage``. Browsers don't forward CAP's
 * ``X-User-Email`` header on plain ``<video src>``, ``<img src>`` or
 * ``<iframe src>`` requests; only cookies. Warranty-claim videos
 * live behind ``/api/warranty/media/{id}`` which requires that
 * header, so a raw ``<video src>`` produced a blank player.
 *
 * This component fetches the media as a Blob through ``fetch`` (with
 * X-User-Email + optional Bearer attached), wraps it in a
 * ``URL.createObjectURL`` blob URL, and renders a standard
 * ``<video controls>``. The blob URL is revoked when the component
 * unmounts so the browser can release the memory.
 *
 * Limitations:
 *   * The entire video is downloaded before playback starts (no
 *     range-streaming). That's fine for the short warranty-claim
 *     clips users record on their phone (typically < 30s, < 20 MB).
 *     For longer videos we'd need a different approach — token-
 *     gated streaming URLs or a service-worker-injected header.
 */
import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function buildAuthHeaders(path) {
  const headers = {};
  let hasUser = false;
  try {
    const saved = localStorage.getItem("usair_user");
    if (saved) {
      const u = JSON.parse(saved);
      if (u?.email) { headers["X-User-Email"] = u.email; hasUser = true; }
    }
  } catch { /* ignore */ }
  // Only fall back to the conv bearer session when there is no
  // portal user — otherwise a stale token left over from an iter124
  // OTP-redeem test session would shadow the real portal auth.
  try {
    const sess = localStorage.getItem("usair_conv_session");
    if (!hasUser && sess && path.startsWith("/conv")) {
      headers["Authorization"] = `Bearer ${sess}`;
    }
  } catch { /* ignore */ }
  return headers;
}

export default function AuthVideo({ src, className = "", testId, controls = true }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [failed, setFailed] = useState("");

  useEffect(() => {
    if (!src) { setBlobUrl(""); setFailed(""); return undefined; }
    let cancelled = false;
    let url = "";
    setFailed("");
    if (src.startsWith("blob:") || src.startsWith("data:")) {
      setBlobUrl(src);
      return undefined;
    }
    if (!src.startsWith("/") && !src.startsWith("http")) {
      setFailed(`Invalid video reference: ${src.slice(0, 40)}`);
      return undefined;
    }
    const apiPath = src.startsWith("/api/") ? src.slice(4) : src;
    const full = src.startsWith("http") ? src : `${BACKEND_URL}/api${apiPath}`;
    fetch(full, {
      credentials: "include",
      headers: buildAuthHeaders(apiPath),
    })
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        setFailed(String(err?.message || "Could not load video"));
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);

  if (failed) {
    return (
      <div data-testid={testId}
        className={`flex flex-col items-center justify-center bg-slate-100 border border-slate-200 text-slate-500 text-[11px] gap-1 px-3 py-4 rounded-md ${className}`}
        title={failed}>
        <span className="text-slate-400 text-base">▶</span>
        <span className="italic">{failed.length > 60 ? failed.slice(0, 57) + "…" : failed}</span>
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div data-testid={testId}
        className={`flex items-center justify-center bg-slate-50 border border-slate-100 text-slate-300 text-[10px] ${className}`}>
        Loading video…
      </div>
    );
  }
  return (
    <video src={blobUrl} controls={controls} className={className} data-testid={testId} />
  );
}
